/**
 * DocsComplianceDialog — R148g · Feb 2026
 * ---------------------------------------
 * Full-workspace snapshot of employee document coverage. HR opens this
 * from the Employees list header to see who's laggging and drill in.
 */
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShieldCheck, ShieldAlert, Loader2, ExternalLink, AlertTriangle, ArrowUpDown } from "lucide-react";

export default function DocsComplianceDialog({ wsId, onClose }) {
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, rows: [], totals: null });
  const [sortBy, setSortBy] = useState("verified_pct");
  const [sortDir, setSortDir] = useState("asc");
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/workspaces/${wsId}/employee-documents/compliance`);
        if (!cancel) setState({ loading: false, rows: data.rows || [], totals: data.totals });
      } catch (e) {
        if (!cancel) {
          toast.error(apiErrorMessage(e, "Failed to load compliance data"));
          setState({ loading: false, rows: [], totals: null });
        }
      }
    })();
    return () => { cancel = true; };
  }, [wsId]);

  const rows = useMemo(() => {
    let list = state.rows.slice();
    const needle = q.trim().toLowerCase();
    if (needle) {
      list = list.filter((r) =>
        (r.user_name || "").toLowerCase().includes(needle) ||
        (r.user_email || "").toLowerCase().includes(needle));
    }
    const dir = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      const A = a[sortBy]; const B = b[sortBy];
      if (typeof A === "string") return dir * A.localeCompare(B || "");
      return dir * ((A ?? 0) - (B ?? 0));
    });
    return list;
  }, [state.rows, q, sortBy, sortDir]);

  const setSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };
  const t = state.totals || {};

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-6xl w-[95vw] max-h-[92vh] overflow-y-auto rounded-none"
        data-testid="docs-compliance-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" /> Documents Compliance Dashboard
          </DialogTitle>
          <DialogDescription>
            Every workspace member with their document uploaded / verified %, sorted least-verified first. Click a row to open that employee.
          </DialogDescription>
        </DialogHeader>

        {state.loading ? (
          <div className="p-8 text-sm text-muted-foreground text-center">
            <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-3">
            {/* Overall KPI */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <Kpi label="Employees" value={t.employees ?? 0} />
              <Kpi label="Avg uploaded" value={`${t.avg_uploaded_pct ?? 0}%`}
                    tone={(t.avg_uploaded_pct ?? 0) >= 95 ? "green" : (t.avg_uploaded_pct ?? 0) >= 60 ? "amber" : "red"} />
              <Kpi label="Avg verified" value={`${t.avg_verified_pct ?? 0}%`}
                    tone={(t.avg_verified_pct ?? 0) >= 95 ? "green" : (t.avg_verified_pct ?? 0) >= 60 ? "amber" : "red"} />
              <Kpi label="Expiring soon" value={t.expiring_soon_total ?? 0}
                    tone={(t.expiring_soon_total ?? 0) > 0 ? "amber" : "green"} />
              <Kpi label="Expired" value={t.expired_total ?? 0}
                    tone={(t.expired_total ?? 0) > 0 ? "red" : "green"} />
            </div>

            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or email…"
              className="w-full border border-border h-8 px-2 text-sm rounded-none bg-background"
              data-testid="docs-compliance-search"
            />

            {/* Rows table */}
            <div className="border border-border overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 border-b border-border">
                  <tr>
                    {[
                      { col: "user_name",     label: "Employee",   align: "left"  },
                      { col: "total",         label: "Rows",       align: "right" },
                      { col: "uploaded_pct",  label: "Uploaded %", align: "right" },
                      { col: "verified_pct",  label: "Verified %", align: "right" },
                      { col: "expiring_soon", label: "Expiring",   align: "right" },
                      { col: "expired",       label: "Expired",    align: "right" },
                    ].map(({ col, label, align }) => (
                      <th
                        key={col}
                        className={`px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground text-${align} cursor-pointer select-none hover:text-foreground`}
                        onClick={() => setSort(col)}
                        data-testid={`docs-comp-sort-${col}`}
                      >
                        {label}
                        {sortBy === col && <ArrowUpDown className="w-3 h-3 inline ml-1" />}
                      </th>
                    ))}
                    <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground text-center">Open</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No matches.</td></tr>
                  ) : rows.map((r) => (
                    <tr key={r.user_id} className="border-b border-border/40 hover:bg-muted/20"
                        data-testid={`docs-comp-row-${r.user_id}`}>
                      <td className="px-3 py-2">
                        <div className="font-semibold truncate">{r.user_name || "—"}</div>
                        <div className="text-[10px] text-muted-foreground truncate">{r.user_email}</div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{r.total}</td>
                      <td className={`px-3 py-2 text-right font-mono font-bold ${r.uploaded_pct >= 95 ? "text-emerald-700" : r.uploaded_pct >= 50 ? "text-amber-700" : "text-rose-700"}`}>
                        {r.uploaded_pct}%
                      </td>
                      <td className={`px-3 py-2 text-right font-mono font-bold ${r.verified_pct >= 95 ? "text-emerald-700" : r.verified_pct >= 50 ? "text-amber-700" : "text-rose-700"}`}>
                        {r.verified_pct}%
                      </td>
                      <td className="px-3 py-2 text-right">
                        {r.expiring_soon > 0 ? (
                          <span className="inline-block border border-amber-500 bg-amber-50 text-amber-800 px-1 text-[10px] font-bold">{r.expiring_soon}</span>
                        ) : <span className="text-muted-foreground/50">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {r.expired > 0 ? (
                          <span className="inline-flex items-center gap-1 border border-rose-500 bg-rose-50 text-rose-800 px-1 text-[10px] font-bold">
                            <AlertTriangle className="w-2.5 h-2.5" /> {r.expired}
                          </span>
                        ) : <span className="text-muted-foreground/50">—</span>}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => { onClose(); nav(`/app/employees/${r.user_id}?tab=documents`); }}
                          className="text-primary hover:underline text-[10px] inline-flex items-center gap-1"
                          data-testid={`docs-comp-open-${r.user_id}`}
                        >
                          Open <ExternalLink className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Kpi({ label, value, tone }) {
  const toneCls = tone === "green" ? "text-emerald-700"
                : tone === "amber" ? "text-amber-700"
                : tone === "red"   ? "text-rose-700"
                : "text-foreground";
  return (
    <div className="border border-border p-2 bg-background">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`text-lg font-bold tabular-nums ${toneCls}`}>{value}</div>
    </div>
  );
}
